import { createContext, createElement, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useTax1099Api } from './api/useTax1099ApiHook';
import { getBranch } from '../../store/user/selectors';

export interface PayerData {
  id?: number;
  payer_legal_name?: string | null;
  payer_dba_name?: string | null;
  payer_tin_type?: number | string | null;
  payer_ein?: string | null;
  payer_masked_ein?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  transmitter_control_code?: string | null;
  apportion_unallocated_income_to_home_state?: boolean;
  state_payer_ids?: Record<string, string | number> | null;
  w9_file_id?: number | null;
  w9_file_name?: string | null;
}

const usePayerDataFetch = () => {
  const branchData: any = useSelector(getBranch);
  const branchId = branchData?.id ?? null;
  const [payerData, setPayerData] = useState<PayerData | null>(null);
  const [isConfigured, setIsConfigured] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);

  const { fetchPayerSettings } = useTax1099Api();

  const applyResponse = (response: any) => {
    const body = response?.data?.data;
    if (body?.settings) {
      setPayerData(body.settings);
      setIsConfigured(true);
    } else if (body?.defaults) {
      setPayerData(body.defaults);
      setIsConfigured(false);
    } else {
      setPayerData(null);
      setIsConfigured(false);
    }
  };

  useEffect(() => {
    if (!branchId) {
      setPayerData(null);
      setIsConfigured(false);
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    fetchPayerSettings(
      { branch_id: branchId },
      (_msg: string, response: any) => {
        setLoading(false);
        applyResponse(response);
      },
      (message: string) => {
        // Previously this only cleared the loading flag. Silence meant the
        // screen fell back to a branch name, or to 'Active Business', with no
        // sign anything had failed -- indistinguishable from an entity that is
        // genuinely named that. One provider means one message, not one per
        // consumer, so saying so is now cheap.
        setLoading(false);
        setLoadFailed(true);
        toast.error(message || 'Could not load the payer profile for this entity.');
      }
    );
  }, [branchId]);

  // Saving or uploading a W-9 changes what the server holds, but this copy is
  // fetched once per branch, so every screen kept showing the old one until the
  // provider remounted. A refresh is quiet: no spinner, and a failure keeps the
  // data already on screen rather than blanking it.
  const refresh = () => {
    if (!branchId) {
      return;
    }
    fetchPayerSettings({ branch_id: branchId }, (_msg: string, response: any) => applyResponse(response), () => {});
  };

  const businessName =
    payerData?.payer_legal_name ||
    payerData?.payer_dba_name ||
    branchData?.business_name ||
    branchData?.name ||
    branchData?.branch_name ||
    'Active Business';

  const maskedEin =
    payerData?.payer_masked_ein ||
    (payerData?.payer_ein
      ? `**-***${payerData.payer_ein.replace(/\D/g, '').slice(-4)}`
      : null);

  const formattedAddress = [
    payerData?.address_line1,
    payerData?.address_line2,
    [payerData?.city, payerData?.state].filter(Boolean).join(', '),
    payerData?.postal_code,
  ]
    .filter(Boolean)
    .join(' ');

  return {
    branchId,
    branchData,
    payerData,
    isConfigured,
    businessName,
    maskedEin,
    formattedAddress,
    w9FileId: payerData?.w9_file_id ?? null,
    w9FileName: payerData?.w9_file_name ?? null,
    hasW9: Boolean(payerData?.w9_file_id),
    loading,
    // True when the fetch failed, so a caller can tell "not configured" apart
    // from "we could not find out".
    loadFailed,
    refresh,
  };
};

type PayerDataValue = ReturnType<typeof usePayerDataFetch>;

const PayerDataContext = createContext<PayerDataValue | null>(null);

/**
 * One fetch of the payer settings per branch, shared by every 1099 screen.
 *
 * The hook used to hold its own state and fire its own request in each caller.
 * With five callers that was five identical requests per page load, and worse,
 * five independent answers: a component whose request failed kept payerData
 * null and rendered the 'Active Business' fallback while the sidebar showed the
 * real name, so one screen displayed two names for the same entity.
 */
export const PayerDataProvider = ({ children }: { children: ReactNode }) => {
  const value = usePayerDataFetch();
  return createElement(PayerDataContext.Provider, { value }, children);
};

export const usePayerData = (): PayerDataValue => {
  const value = useContext(PayerDataContext);
  if (!value) {
    // Throwing beats silently returning defaults: the fallback name looks like
    // a real business, so a missing provider would ship as wrong data, not as
    // an obvious failure.
    throw new Error('usePayerData must be used inside <PayerDataProvider>');
  }
  return value;
};

export default usePayerData;
